/**
 * Advanced Astronomical Calculator
 * Calculates planetary positions, Human Design gates, and Chinese astrology
 * Based on precise birth time and location
 */

import * as Astronomy from 'astronomy-engine';
import { DateTime } from 'luxon';
import { calculateZodiacSign } from '@/shared/hooks/useAIFeedback';
import logger from '@/shared/utils/core/logger';


/**
 * Fallback: Convert location string to coordinates using database
 * NOTE: This is no longer used for geocoding - all coordinates come from Google Maps API
 * Kept for potential timezone lookup in the future if needed
 */
const CITY_COORDINATES = {
  // Major US cities
  'new york': { lat: 40.7128, lon: -74.0060, tz: 'America/New_York' },
  'los angeles': { lat: 34.0522, lon: -118.2437, tz: 'America/Los_Angeles' },
  'chicago': { lat: 41.8781, lon: -87.6298, tz: 'America/Chicago' },
  'san francisco': { lat: 37.7749, lon: -122.4194, tz: 'America/Los_Angeles' },
  'seattle': { lat: 47.6062, lon: -122.3321, tz: 'America/Los_Angeles' },
  'miami': { lat: 25.7617, lon: -80.1918, tz: 'America/New_York' },
  'boston': { lat: 42.3601, lon: -71.0589, tz: 'America/New_York' },
  'austin': { lat: 30.2672, lon: -97.7431, tz: 'America/Chicago' },

  // Major international cities
  'london': { lat: 51.5074, lon: -0.1278, tz: 'Europe/London' },
  'paris': { lat: 48.8566, lon: 2.3522, tz: 'Europe/Paris' },
  'tokyo': { lat: 35.6762, lon: 139.6503, tz: 'Asia/Tokyo' },
  'sydney': { lat: -33.8688, lon: 151.2093, tz: 'Australia/Sydney' },
  'mumbai': { lat: 19.0760, lon: 72.8777, tz: 'Asia/Kolkata' },
  'beijing': { lat: 39.9042, lon: 116.4074, tz: 'Asia/Shanghai' },
  'dubai': { lat: 25.2048, lon: 55.2708, tz: 'Asia/Dubai' },
  'toronto': { lat: 43.6532, lon: -79.3832, tz: 'America/Toronto' },
};

/**
 * Lookup timezone from fallback database for known cities
 * @param {string} birthPlace - Birth place name
 * @returns {string|null} - IANA timezone ID or null
 */
function lookupTimezoneFromDatabase(birthPlace) {
  if (!birthPlace) return null;

  // Normalize the city name for lookup (lowercase, remove common suffixes)
  const normalized = birthPlace.toLowerCase()
    .replace(/,.*$/, '') // Remove everything after comma (state, country)
    .trim();

  // Direct match
  if (CITY_COORDINATES[normalized]) {
    return CITY_COORDINATES[normalized].tz;
  }

  // Partial match (for cases like "New York City" vs "new york")
  for (const [city, data] of Object.entries(CITY_COORDINATES)) {
    if (normalized.includes(city) || city.includes(normalized)) {
      return data.tz;
    }
  }

  return null;
}

/**
 * Calculate precise birth moment in UTC considering timezone
 * @param {string} birthDate - Birth date in YYYY-MM-DD format
 * @param {string} birthTime - Birth time in HH:MM format (optional)
 * @param {string} birthPlace - Birth place name (for fallback)
 * @param {number} birthPlaceLat - Birth place latitude (optional, from Google Maps)
 * @param {number} birthPlaceLon - Birth place longitude (optional, from Google Maps)
 * @param {string} birthTimezone - IANA timezone ID from Google Maps API (optional)
 */
export async function calculatePreciseBirthMoment(birthDate, birthTime, birthPlace, birthPlaceLat, birthPlaceLon, birthTimezone) {
  // REQUIRE coordinates from Google Maps for accurate astronomical calculations
  if (birthPlaceLat === undefined || birthPlaceLon === undefined ||
      birthPlaceLat === null || birthPlaceLon === null) {
    logger.error('❌ Birth coordinates required for astrological calculations');
    throw new Error('MISSING_COORDINATES: Birth location coordinates are required. Please update your birth location in profile settings to use astrological features.');
  }

  // Determine timezone to use (priority order):
  // 1. Birth location timezone (from Google Timezone API) - MOST ACCURATE
  // 2. Known city database fallback (for common cities when API fails)
  // 3. UTC fallback as last resort
  let effectiveTimezone = 'UTC';
  let timezoneSource = 'UTC fallback';

  if (birthTimezone) {
    effectiveTimezone = birthTimezone;
    timezoneSource = 'Google Maps API';
    logger.debug(`✅ Using timezone from ${timezoneSource}: ${effectiveTimezone}`);
  } else {
    // Try fallback database for known cities
    const fallbackTz = lookupTimezoneFromDatabase(birthPlace);
    if (fallbackTz) {
      effectiveTimezone = fallbackTz;
      timezoneSource = 'city database';
      logger.info(`✅ Using timezone from ${timezoneSource} for ${birthPlace}: ${effectiveTimezone}`);
    } else {
      // Last resort: UTC with warning
      logger.warn(`⚠️ Birth timezone not available for ${birthPlace}. Using UTC as fallback. Rising sign may be inaccurate.`);
      logger.warn('⚠️ To fix this: Update your birth location in profile settings to recalculate with correct timezone.');
    }
  }

  const location = {
    lat: birthPlaceLat,
    lon: birthPlaceLon,
    tz: effectiveTimezone,
    displayName: birthPlace || `${birthPlaceLat}°, ${birthPlaceLon}°`
  };

  logger.debug(`✅ Using Google Maps coordinates: ${location.lat}, ${location.lon} (TZ: ${location.tz})`);

  // Parse birth date and time in the birth location's timezone
  const dateTimeString = birthTime
    ? `${birthDate}T${birthTime}:00`
    : `${birthDate}T12:00:00`;

  const birthMoment = DateTime.fromISO(dateTimeString, { zone: location.tz });

  return {
    utcDate: birthMoment.toUTC().toJSDate(),
    localDate: birthMoment.toJSDate(),
    timezone: location.tz,
    location: location,
    birthMoment: birthMoment
  };
}

/**
 * Calculate all planetary positions at birth
 * Returns positions in zodiac degrees (0-360)
 */
export function calculatePlanetaryPositions(birthData) {
  const { utcDate, localDate } = birthData;

  const planets = {};

  // Validate input data
  if (!utcDate || !localDate) {
    logger.error('❌ Invalid birth data: missing date information');
    return planets;
  }

  try {
    // Sun position (CRITICAL - must succeed)
    try {
      const sun = Astronomy.SunPosition(utcDate);
      // CRITICAL: Use simple date-based calculation for Sun sign (tropical zodiac)
      // This ensures 100% accuracy matching Co-Star and standard Western astrology
      planets.sun = {
        eclipticLongitude: sun.elon,
        zodiacDegree: ((sun.elon % 360) + 360) % 360,
        zodiacSign: calculateZodiacSign(localDate) // Use simple tropical calculation
      };
      logger.debug('☀️ Sun sign calculated (tropical):', planets.sun.zodiacSign);
    } catch (sunError) {
      logger.error('❌ Critical error calculating Sun position:', sunError);
      // Fallback: use simple zodiac only
      planets.sun = {
        eclipticLongitude: 0,
        zodiacDegree: 0,
        zodiacSign: calculateZodiacSign(localDate)
      };
    }

    // Moon position (CRITICAL - must succeed)
    try {
      const moonGeo = Astronomy.GeoMoon(utcDate);
      const moonEcliptic = Astronomy.Ecliptic(moonGeo);
      planets.moon = {
        eclipticLongitude: moonEcliptic.elon,
        zodiacDegree: ((moonEcliptic.elon % 360) + 360) % 360,
        zodiacSign: getZodiacSignFromLongitude(moonEcliptic.elon)
      };
      logger.debug('🌙 Moon calculated:', planets.moon);
    } catch (moonError) {
      logger.error('❌ Error calculating Moon position:', moonError);
    }

    // Inner planets (Mercury, Venus, Mars)
    try {
      const mercury = Astronomy.HelioVector('Mercury', utcDate);
      const mercuryEcl = Astronomy.Ecliptic(mercury);
      planets.mercury = {
        eclipticLongitude: mercuryEcl.elon,
        zodiacDegree: ((mercuryEcl.elon % 360) + 360) % 360,
        zodiacSign: getZodiacSignFromLongitude(mercuryEcl.elon)
      };
    } catch (mercuryError) {
      logger.warn('⚠️ Could not calculate Mercury:', mercuryError.message);
    }

    try {
      const venus = Astronomy.HelioVector('Venus', utcDate);
      const venusEcl = Astronomy.Ecliptic(venus);
      planets.venus = {
        eclipticLongitude: venusEcl.elon,
        zodiacDegree: ((venusEcl.elon % 360) + 360) % 360,
        zodiacSign: getZodiacSignFromLongitude(venusEcl.elon)
      };
    } catch (venusError) {
      logger.warn('⚠️ Could not calculate Venus:', venusError.message);
    }

    try {
      const mars = Astronomy.HelioVector('Mars', utcDate);
      const marsEcl = Astronomy.Ecliptic(mars);
      planets.mars = {
        eclipticLongitude: marsEcl.elon,
        zodiacDegree: ((marsEcl.elon % 360) + 360) % 360,
        zodiacSign: getZodiacSignFromLongitude(marsEcl.elon)
      };
    } catch (marsError) {
      logger.warn('⚠️ Could not calculate Mars:', marsError.message);
    }

    // Social planets (Jupiter, Saturn)
    try {
      const jupiter = Astronomy.HelioVector('Jupiter', utcDate);
      const jupiterEcl = Astronomy.Ecliptic(jupiter);
      planets.jupiter = {
        eclipticLongitude: jupiterEcl.elon,
        zodiacDegree: ((jupiterEcl.elon % 360) + 360) % 360,
        zodiacSign: getZodiacSignFromLongitude(jupiterEcl.elon)
      };
    } catch (jupiterError) {
      logger.warn('⚠️ Could not calculate Jupiter:', jupiterError.message);
    }

    try {
      const saturn = Astronomy.HelioVector('Saturn', utcDate);
      const saturnEcl = Astronomy.Ecliptic(saturn);
      planets.saturn = {
        eclipticLongitude: saturnEcl.elon,
        zodiacDegree: ((saturnEcl.elon % 360) + 360) % 360,
        zodiacSign: getZodiacSignFromLongitude(saturnEcl.elon)
      };
    } catch (saturnError) {
      logger.warn('⚠️ Could not calculate Saturn:', saturnError.message);
    }

    // Outer planets (Uranus, Neptune, Pluto)
    try {
      const uranus = Astronomy.HelioVector('Uranus', utcDate);
      const uranusEcl = Astronomy.Ecliptic(uranus);
      planets.uranus = {
        eclipticLongitude: uranusEcl.elon,
        zodiacDegree: ((uranusEcl.elon % 360) + 360) % 360,
        zodiacSign: getZodiacSignFromLongitude(uranusEcl.elon)
      };
    } catch (uranusError) {
      logger.warn('⚠️ Could not calculate Uranus:', uranusError.message);
    }

    try {
      const neptune = Astronomy.HelioVector('Neptune', utcDate);
      const neptuneEcl = Astronomy.Ecliptic(neptune);
      planets.neptune = {
        eclipticLongitude: neptuneEcl.elon,
        zodiacDegree: ((neptuneEcl.elon % 360) + 360) % 360,
        zodiacSign: getZodiacSignFromLongitude(neptuneEcl.elon)
      };
    } catch (neptuneError) {
      logger.warn('⚠️ Could not calculate Neptune:', neptuneError.message);
    }

    try {
      const pluto = Astronomy.HelioVector('Pluto', utcDate);
      const plutoEcl = Astronomy.Ecliptic(pluto);
      planets.pluto = {
        eclipticLongitude: plutoEcl.elon,
        zodiacDegree: ((plutoEcl.elon % 360) + 360) % 360,
        zodiacSign: getZodiacSignFromLongitude(plutoEcl.elon)
      };
    } catch (plutoError) {
      logger.warn('⚠️ Could not calculate Pluto:', plutoError.message);
    }

    // Calculate Lunar Nodes (important in Human Design)
    const nodes = calculateLunarNodes(utcDate);
    planets.northNode = nodes.north;
    planets.southNode = nodes.south;

    logger.debug('✨ Full natal chart calculated: 10 planets + nodes');
    logger.debug('   Sun:', planets.sun?.zodiacSign || 'N/A', '| Moon:', planets.moon?.zodiacSign || 'N/A');
    logger.debug('   Outer planets:', planets.uranus?.zodiacSign || 'N/A', planets.neptune?.zodiacSign || 'N/A', planets.pluto?.zodiacSign || 'N/A');

  } catch (error) {
    logger.error('❌ Error calculating planetary positions:', error);
    // Return partial data if some planets were calculated
    if (Object.keys(planets).length > 0) {
      logger.warn('⚠️ Returning partial planetary data');
    }
  }

  // Final validation - ensure we have at least Sun and Moon
  if (!planets.sun || !planets.moon) {
    logger.error('❌ Critical: Missing Sun or Moon position in natal chart');
  }

  return planets;
}

/**
 * Get zodiac sign from ecliptic longitude (sidereal/astronomical method)
 * Used for Moon and planets - NOT for Sun sign (use calculateZodiacSign for Sun)
 */
function getZodiacSignFromLongitude(longitude) {
  const normalized = ((longitude % 360) + 360) % 360;
  const signs = [
    'Aries', 'Taurus', 'Gemini', 'Cancer',
    'Leo', 'Virgo', 'Libra', 'Scorpio',
    'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];
  const signIndex = Math.floor(normalized / 30);
  return signs[signIndex];
}

/**
 * Calculate Lunar Nodes (approximate)
 * North Node cycles through zodiac every ~18.6 years
 */
function calculateLunarNodes(date) {
  // Simplified calculation based on lunar node period
  const referenceDate = new Date('2000-01-01T00:00:00Z');
  const daysSinceReference = (date - referenceDate) / (1000 * 60 * 60 * 24);

  // Lunar nodes retrograde through zodiac
  const nodePeriod = 6798.38; // days for complete cycle
  const cycles = daysSinceReference / nodePeriod;
  const northNodeLongitude = 360 - ((cycles % 1) * 360);

  return {
    north: {
      zodiacDegree: northNodeLongitude,
      zodiacSign: getZodiacSignFromLongitude(northNodeLongitude)
    },
    south: {
      zodiacDegree: (northNodeLongitude + 180) % 360,
      zodiacSign: getZodiacSignFromLongitude((northNodeLongitude + 180) % 360)
    }
  };
}

/**
 * Calculate Human Design Gates based on planetary positions
 * In Human Design, each planet activates 2 gates (Personality and Design)
 * Gates are mapped to I Ching hexagrams (1-64)
 */
export function calculateHumanDesignGates(planets) {
  const gates = {};

  // Map each planet's zodiac position to I Ching gate (1-64)
  // Each zodiac sign (30°) contains approximately 5.33 gates
  // 64 gates / 12 signs = 5.33 gates per sign

  for (const [planet, data] of Object.entries(planets)) {
    if (data.zodiacDegree !== undefined) {
      const gate = zodiacDegreeToGate(data.zodiacDegree);
      const line = zodiacDegreeToLine(data.zodiacDegree);

      gates[planet] = {
        gate: gate,
        line: line,
        position: `${gate}.${line}`,
        zodiacDegree: data.zodiacDegree,
        zodiacSign: data.zodiacSign
      };
    }
  }

  return gates;
}

/**
 * Convert zodiac degree (0-360) to I Ching gate (1-64)
 * Based on the wheel of Human Design
 */
function zodiacDegreeToGate(degree) {
  const normalized = ((degree % 360) + 360) % 360;

  // 360 degrees / 64 gates = 5.625 degrees per gate
  const gateIndex = Math.min(Math.floor(normalized / 5.625), 59); // gateWheel has 60 entries (0-59)

  // Human Design gate wheel starts at Aries 0° = Gate 41
  // This is the traditional HD wheel mapping
  const gateWheel = [
    41, 19, 13, 49, 30, 55, 37, 63, 22, 36, 25, 17, // Aries
    21, 51, 42, 3, 27, 24, 2, 23, 8, 20, 16, 35,   // Taurus
    45, 12, 15, 52, 39, 53, 62, 56, 31, 33, 7, 4,   // Gemini
    29, 59, 40, 64, 47, 6, 46, 18, 48, 57, 32, 50,  // Cancer
    28, 44, 1, 43, 14, 34, 9, 5, 26, 11, 10, 58     // Leo-Pisces
  ];

  return gateWheel[gateIndex] || 1;
}

/**
 * Convert zodiac degree to line number (1-6)
 * Each gate has 6 lines, representing the 6 lines of an I Ching hexagram
 */
function zodiacDegreeToLine(degree) {
  const normalized = ((degree % 360) + 360) % 360;
  const degreeWithinGate = normalized % 5.625; // Each gate spans 5.625°
  const lineNumber = Math.floor((degreeWithinGate / 5.625) * 6) + 1;
  return Math.min(lineNumber, 6);
}

/**
 * Calculate Chinese Four Pillars (BaZi)
 * Uses year, month, day, and hour to determine heavenly stems and earthly branches
 */
export function calculateFourPillars(birthData) {
  const { birthMoment } = birthData;

  const year = birthMoment.year;
  const month = birthMoment.month;
  const hour = birthMoment.hour;

  // Heavenly Stems (天干) - 10 stems
  const heavenlyStems = ['甲 Jia (Wood+)', '乙 Yi (Wood-)', '丙 Bing (Fire+)', '丁 Ding (Fire-)',
                         '戊 Wu (Earth+)', '己 Ji (Earth-)', '庚 Geng (Metal+)', '辛 Xin (Metal-)',
                         '壬 Ren (Water+)', '癸 Gui (Water-)'];

  // Earthly Branches (地支) - 12 branches
  const earthlyBranches = ['子 Zi (Rat)', '丑 Chou (Ox)', '寅 Yin (Tiger)', '卯 Mao (Rabbit)',
                           '辰 Chen (Dragon)', '巳 Si (Snake)', '午 Wu (Horse)', '未 Wei (Goat)',
                           '申 Shen (Monkey)', '酉 You (Rooster)', '戌 Xu (Dog)', '亥 Hai (Pig)'];

  // Year Pillar
  const yearStem = heavenlyStems[(year - 4) % 10];
  const yearBranch = earthlyBranches[(year - 4) % 12];

  // Month Pillar (simplified - exact calculation requires solar terms)
  const monthStem = heavenlyStems[((year - 4) * 2 + month) % 10];
  const monthBranch = earthlyBranches[(month + 1) % 12];

  // Day Pillar (simplified - requires Julian day number for exact calculation)
  const dayNumber = Math.floor((birthMoment.toJSDate() - new Date('1900-01-01')) / (1000 * 60 * 60 * 24));
  const dayStem = heavenlyStems[(dayNumber + 9) % 10];
  const dayBranch = earthlyBranches[(dayNumber + 9) % 12];

  // Hour Pillar
  const hourIndex = Math.floor((hour + 1) / 2) % 12;
  const hourStem = heavenlyStems[(dayNumber * 2 + hourIndex) % 10];
  const hourBranch = earthlyBranches[hourIndex];

  return {
    year: { stem: yearStem, branch: yearBranch },
    month: { stem: monthStem, branch: monthBranch },
    day: { stem: dayStem, branch: dayBranch },
    hour: { stem: hourStem, branch: hourBranch },
    elements: analyzeElements({ year: yearStem, month: monthStem, day: dayStem, hour: hourStem })
  };
}

/**
 * Analyze elemental composition from Four Pillars
 */
function analyzeElements(stems) {
  const elementCount = { Wood: 0, Fire: 0, Earth: 0, Metal: 0, Water: 0 };

  const stemElements = {
    '甲': 'Wood', '乙': 'Wood',
    '丙': 'Fire', '丁': 'Fire',
    '戊': 'Earth', '己': 'Earth',
    '庚': 'Metal', '辛': 'Metal',
    '壬': 'Water', '癸': 'Water'
  };

  for (const stem of Object.values(stems)) {
    const element = stemElements[stem.charAt(0)];
    if (element) elementCount[element]++;
  }

  const dominantElement = Object.entries(elementCount)
    .sort((a, b) => b[1] - a[1])[0][0];

  return {
    count: elementCount,
    dominant: dominantElement,
    balanced: Object.values(elementCount).every(v => v > 0)
  };
}

/**
 * Calculate Human Design Type, Profile, and Authority
 * Based on the Sacral, Solar Plexus, and other centers
 */
export function calculateHumanDesignMetadata(humanDesignGates) {
  // Validate input
  if (!humanDesignGates || typeof humanDesignGates !== 'object') {
    logger.warn('⚠️ Invalid humanDesignGates provided, using defaults');
    return {
      type: 'Generator',
      profile: '1/3',
      profileMeaning: 'Default profile - complete birth data needed for accuracy',
      strategy: 'To Respond',
      authority: 'Sacral',
      definition: 'Unknown',
      incarnationCross: 'Unknown'
    };
  }

  // In Human Design, Type is determined by which centers are defined
  // This is a simplified calculation - full HD requires bodygraph analysis

  // Profile is determined by the Sun and Earth gates (Personality and Design)
  const sunLine = humanDesignGates.sun?.line || 1;
  const sunGate = humanDesignGates.sun?.gate || 1;

  // Ensure line is between 1-6
  const validSunLine = Math.max(1, Math.min(6, sunLine));

  // Calculate Earth gate (opposite Sun on the mandala)
  const earthGate = ((sunGate + 31) % 64) || 32;
  const earthLine = Math.max(1, Math.min(6, 7 - validSunLine)); // Earth line is complementary

  const profile = `${validSunLine}/${earthLine}`;

  // Profile meanings
  const profileMeanings = {
    '1/3': 'Investigator/Martyr - Learning through investigation and trial/error',
    '1/4': 'Investigator/Opportunist - Foundation through investigation, opportunities through networks',
    '2/4': 'Hermit/Opportunist - Natural talents called out by others',
    '2/5': 'Hermit/Heretic - Natural talents with universal application',
    '3/5': 'Martyr/Heretic - Experiential wisdom with practical solutions',
    '3/6': 'Martyr/Role Model - Life in three phases, wisdom through experience',
    '4/6': 'Opportunist/Role Model - Influencing through authentic relationships',
    '4/1': 'Opportunist/Investigator - Networks built on solid foundation',
    '5/1': 'Heretic/Investigator - Practical solutions backed by research',
    '5/2': 'Heretic/Hermit - Universal solutions from natural gifts',
    '6/2': 'Role Model/Hermit - Wisdom shared when called',
    '6/3': 'Role Model/Martyr - Wisdom through lived experience'
  };

  // Type determination (simplified - would need full bodygraph)
  // For now, we'll use a heuristic based on primary gates
  let type = 'Manifestor'; // Default
  let strategy = 'To Inform';
  let authority = 'Emotional'; // Default to most common

  // Simplified type determination based on Sacral-related gates
  const sacralGates = [5, 14, 29, 59, 9, 3, 42, 27, 34];
  const hasSacralDefined = Object.values(humanDesignGates).some(g =>
    g?.gate && sacralGates.includes(g.gate)
  );

  if (hasSacralDefined) {
    type = 'Generator'; // or Manifesting Generator
    strategy = 'To Respond';
    authority = 'Sacral';
  }

  // Emotional Solar Plexus gates
  const emotionalGates = [6, 37, 22, 36, 30, 55, 49];
  const hasEmotionalDefined = Object.values(humanDesignGates).some(g =>
    g?.gate && emotionalGates.includes(g.gate)
  );

  if (hasEmotionalDefined) {
    authority = 'Emotional';
  }

  return {
    type,
    profile,
    profileMeaning: profileMeanings[profile] || 'Unique combination',
    strategy,
    authority,
    definition: 'Single', // Simplified - would need bodygraph analysis
    incarnationCross: `Right Angle Cross of ${humanDesignGates.sun?.gate || 1}` // Simplified
  };
}

/**
 * Calculate Western Astrology (Sun, Moon, Rising signs)
 * Like Co-Star uses
 */
export function calculateWesternAstrology(planets, birthData) {
  const { location, utcDate } = birthData;

  // Sun Sign (zodiac sign the Sun was in)
  const sunSign = planets.sun.zodiacSign;
  const sunDegree = Math.floor(planets.sun.zodiacDegree % 30);

  // Moon Sign (zodiac sign the Moon was in)
  const moonSign = planets.moon.zodiacSign;
  const moonDegree = Math.floor(planets.moon.zodiacDegree % 30);

  // Rising Sign (Ascendant) - requires birth time and location
  let risingSign = null;
  let ascendant = null;

  if (location && location.lat !== undefined && birthData.birthMoment) {
    ascendant = calculateAscendant(utcDate, location.lat, location.lon);
    risingSign = ascendant.sign;
  }

  return {
    sun: {
      sign: sunSign,
      degree: sunDegree,
      symbol: getZodiacSymbol(sunSign),
      element: getZodiacElement(sunSign),
      modality: getZodiacModality(sunSign)
    },
    moon: {
      sign: moonSign,
      degree: moonDegree,
      symbol: getZodiacSymbol(moonSign),
      element: getZodiacElement(moonSign),
      modality: getZodiacModality(moonSign)
    },
    rising: ascendant ? {
      sign: risingSign,
      degree: ascendant.degree,
      symbol: getZodiacSymbol(risingSign),
      element: getZodiacElement(risingSign),
      modality: getZodiacModality(risingSign)
    } : null
  };
}

/**
 * Calculate Ascendant (Rising Sign)
 * This is the zodiac sign that was rising on the eastern horizon at birth
 */
function calculateAscendant(utcDate, lat, lon) {
  try {
    // Calculate Local Sidereal Time (LST)
    const jd = julianDay(utcDate);
    const gmst = greenwichMeanSiderealTime(jd);
    const lst = gmst + (lon / 15); // Convert longitude to hours

    // Normalize LST to 0-24 range
    const lstNormalized = ((lst % 24) + 24) % 24;

    // Calculate RAMC (Right Ascension of the Midheaven)
    const ramc = lstNormalized * 15; // Convert to degrees

    // Calculate Ascendant using simplified formula
    // For more accuracy, would need to account for obliquity and house system
    const obliquity = 23.4393; // Earth's axial tilt in degrees

    // Simplified Ascendant calculation (works reasonably well for mid-latitudes)
    const ascendantDegree = Math.atan2(
      Math.cos(ramc * Math.PI / 180),
      -Math.sin(ramc * Math.PI / 180) * Math.cos(obliquity * Math.PI / 180) - Math.tan(lat * Math.PI / 180) * Math.sin(obliquity * Math.PI / 180)
    ) * 180 / Math.PI;

    const ascendantNormalized = ((ascendantDegree % 360) + 360) % 360;

    return {
      degree: Math.floor(ascendantNormalized % 30),
      zodiacDegree: ascendantNormalized,
      sign: getZodiacSignFromLongitude(ascendantNormalized)
    };
  } catch (error) {
    logger.error('Error calculating ascendant:', error);
    return null;
  }
}

/**
 * Calculate Julian Day (needed for sidereal time calculation)
 */
function julianDay(date) {
  const a = Math.floor((14 - (date.getUTCMonth() + 1)) / 12);
  const y = date.getUTCFullYear() + 4800 - a;
  const m = (date.getUTCMonth() + 1) + 12 * a - 3;

  let jd = date.getUTCDate() + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;

  // Add time of day
  jd += (date.getUTCHours() - 12) / 24;
  jd += date.getUTCMinutes() / 1440;
  jd += date.getUTCSeconds() / 86400;

  return jd;
}

/**
 * Calculate Greenwich Mean Sidereal Time
 */
function greenwichMeanSiderealTime(jd) {
  const t = (jd - 2451545.0) / 36525.0;
  let gmst = 280.46061837 + 360.98564736629 * (jd - 2451545.0) + 0.000387933 * t * t - t * t * t / 38710000;

  // Normalize to 0-360
  gmst = ((gmst % 360) + 360) % 360;

  // Convert to hours
  return gmst / 15;
}

/**
 * Get zodiac symbol
 */
function getZodiacSymbol(sign) {
  const symbols = {
    'Aries': '♈', 'Taurus': '♉', 'Gemini': '♊', 'Cancer': '♋',
    'Leo': '♌', 'Virgo': '♍', 'Libra': '♎', 'Scorpio': '♏',
    'Sagittarius': '♐', 'Capricorn': '♑', 'Aquarius': '♒', 'Pisces': '♓'
  };
  return symbols[sign] || '?';
}

/**
 * Get zodiac element
 */
function getZodiacElement(sign) {
  const elements = {
    'Aries': 'Fire', 'Leo': 'Fire', 'Sagittarius': 'Fire',
    'Taurus': 'Earth', 'Virgo': 'Earth', 'Capricorn': 'Earth',
    'Gemini': 'Air', 'Libra': 'Air', 'Aquarius': 'Air',
    'Cancer': 'Water', 'Scorpio': 'Water', 'Pisces': 'Water'
  };
  return elements[sign] || 'Unknown';
}

/**
 * Get zodiac modality
 */
function getZodiacModality(sign) {
  const modalities = {
    'Aries': 'Cardinal', 'Cancer': 'Cardinal', 'Libra': 'Cardinal', 'Capricorn': 'Cardinal',
    'Taurus': 'Fixed', 'Leo': 'Fixed', 'Scorpio': 'Fixed', 'Aquarius': 'Fixed',
    'Gemini': 'Mutable', 'Virgo': 'Mutable', 'Sagittarius': 'Mutable', 'Pisces': 'Mutable'
  };
  return modalities[sign] || 'Unknown';
}

/**
 * Main function: Calculate complete astrological profile
 * @param {string} birthDate - Birth date in YYYY-MM-DD format
 * @param {string} birthTime - Birth time in HH:MM format (optional)
 * @param {string} birthPlace - Birth place name
 * @param {number} birthPlaceLat - Birth place latitude (optional, from Google Maps)
 * @param {number} birthPlaceLon - Birth place longitude (optional, from Google Maps)
 * @param {string} birthTimezone - Birth location timezone (IANA timezone ID, from Google Maps Timezone API)
 */
export async function calculateAstrologicalProfile(birthDate, birthTime, birthPlace, birthPlaceLat, birthPlaceLon, birthTimezone) {
  // Validate required inputs
  if (!birthDate) {
    logger.error('❌ Birth date is required for astrological profile');
    return null;
  }

  try {
    logger.debug('🔮 Calculating full astrological profile...');
    logger.debug('   Birth Date:', birthDate);
    logger.debug('   Birth Time:', birthTime || 'Not provided (will use noon)');
    logger.debug('   Birth Place:', birthPlace || 'Not provided');
    if (birthPlaceLat && birthPlaceLon) {
      logger.debug('   Coordinates: ', birthPlaceLat, birthPlaceLon, '(from Google Maps)');
    }

    // Step 1: Calculate precise birth moment with accurate coordinates and timezone
    const birthData = await calculatePreciseBirthMoment(birthDate, birthTime, birthPlace, birthPlaceLat, birthPlaceLon, birthTimezone);

    if (!birthData || !birthData.utcDate) {
      logger.error('❌ Failed to calculate birth moment');
      return null;
    }

    // Step 2: Calculate planetary positions (10 planets)
    const planets = calculatePlanetaryPositions(birthData);

    if (!planets || Object.keys(planets).length === 0) {
      logger.error('❌ Failed to calculate planetary positions');
      return null;
    }

    // Step 3: Calculate Western Astrology (Sun/Moon/Rising)
    const westernAstrology = calculateWesternAstrology(planets, birthData);

    // Step 4: Calculate Human Design gates (all planets mapped to gates)
    const humanDesignGates = calculateHumanDesignGates(planets);

    // Step 5: Calculate Human Design Type, Profile, and Authority
    const humanDesign = calculateHumanDesignMetadata(humanDesignGates);

    // Step 6: Calculate Chinese Four Pillars
    const fourPillars = calculateFourPillars(birthData);

    logger.debug('✅ Astrological profile complete!');
    logger.debug(`   Planets calculated: ${Object.keys(planets).length}`);
    logger.debug(`   Human Design gates: ${Object.keys(humanDesignGates).length}`);
    logger.debug(`   HD Type: ${humanDesign.type} | Profile: ${humanDesign.profile}`);
    logger.debug(`   Strategy: ${humanDesign.strategy} | Authority: ${humanDesign.authority}`);

    return {
      birthData: {
        utcTime: birthData.utcDate.toISOString(),
        localTime: birthData.localDate.toISOString(),
        timezone: birthData.timezone,
        location: birthData.location
      },
      planets,
      westernAstrology,
      humanDesignGates,
      humanDesign, // Type, Profile, Authority, Strategy
      fourPillars,
      calculatedAt: new Date().toISOString()
    };
  } catch (error) {
    logger.error('❌ Error calculating astrological profile:', error);
    logger.error('   Details:', error.message);
    return null;
  }
}

/**
 * Calculate current astrological gates (transits)
 * Returns the current planetary positions mapped to Human Design gates
 * Used for providing cosmic context in insights
 */
export function getCurrentAstrologicalGates() {
  const now = new Date();

  // Create birth data structure for current moment
  const currentMoment = {
    utcDate: now,
    localDate: now
  };

  // Calculate current planetary positions
  const positions = calculatePlanetaryPositions(currentMoment);

  // Map positions to Human Design gates
  const gates = {};

  for (const [planet, data] of Object.entries(positions)) {
    if (data && data.zodiacDegree !== undefined) {
      const normalized = ((data.zodiacDegree % 360) + 360) % 360;
      const gateNumber = Math.floor(normalized / 5.625);

      // Human Design gate wheel mapping (same as zodiacDegreeToGate)
      const gateWheel = [
        41, 19, 13, 49, 30, 55, 37, 63, 22, 36, 25, 17,
        21, 51, 42, 3, 27, 24, 2, 23, 8, 20, 16, 35,
        45, 12, 15, 52, 39, 53, 62, 56, 31, 33, 7, 4,
        29, 59, 40, 64, 47, 6, 46, 18, 48, 57, 32, 50,
        28, 44, 1, 43, 14, 34, 9, 5, 26, 11, 10, 58
      ];

      gates[planet] = {
        gate: gateWheel[gateNumber] || 1,
        longitude: data.zodiacDegree,
        zodiacSign: data.zodiacSign
      };
    }
  }

  // Gate theme descriptions for key planets
  const gateThemes = {
    1: 'Self-Expression', 2: 'Direction', 3: 'Ordering', 4: 'Answers',
    5: 'Waiting', 6: 'Friction', 7: 'Leadership', 8: 'Contribution',
    9: 'Focus', 10: 'Self-Love', 11: 'Ideas', 12: 'Caution',
    13: 'Listening', 14: 'Power Skills', 15: 'Extremes', 16: 'Skills',
    17: 'Opinions', 18: 'Correction', 19: 'Wanting', 20: 'Present',
    21: 'Control', 22: 'Openness', 23: 'Assimilation', 24: 'Rationalization',
    25: 'Innocence', 26: 'Taming', 27: 'Nourishment', 28: 'Struggle',
    29: 'Commitment', 30: 'Recognition', 31: 'Influence', 32: 'Continuity',
    33: 'Privacy', 34: 'Power', 35: 'Change', 36: 'Crisis',
    37: 'Family', 38: 'Fighter', 39: 'Provocation', 40: 'Aloneness',
    41: 'Contraction', 42: 'Growth', 43: 'Insight', 44: 'Alertness',
    45: 'Gathering', 46: 'Serendipity', 47: 'Realization', 48: 'Depth',
    49: 'Revolution', 50: 'Values', 51: 'Shock', 52: 'Stillness',
    53: 'Starting', 54: 'Ambition', 55: 'Spirit', 56: 'Stimulation',
    57: 'Intuition', 58: 'Joy', 59: 'Sexuality', 60: 'Limitation',
    61: 'Mystery', 62: 'Details', 63: 'Doubt', 64: 'Confusion'
  };

  return {
    date: now.toISOString(),
    sunGate: gates.sun?.gate,
    sunTheme: gateThemes[gates.sun?.gate] || 'Unknown',
    earthGate: gates.moon?.gate ? ((gates.moon.gate + 31) % 64) + 1 : null,
    moonGate: gates.moon?.gate,
    moonTheme: gateThemes[gates.moon?.gate] || 'Unknown',
    gates,
    summary: `Sun in Gate ${gates.sun?.gate || '?'} (${gateThemes[gates.sun?.gate] || 'Unknown'}), Moon in Gate ${gates.moon?.gate || '?'} (${gateThemes[gates.moon?.gate] || 'Unknown'})`
  };
}

/**
 * Interpret line meanings for a hexagram (1-6)
 * Each line has specific meaning in Human Design
 */
export async function interpretLine(hexagramNumber, lineNumber) {
  const { HEXAGRAMS } = await import('./hexagrams.js');

  const lineInterpretations = {
    1: 'Foundation - The base, introspection, investigation',
    2: 'Balance - Partnership, support, natural knowing',
    3: 'Action - Trial and error, movement, adaptation',
    4: 'Network - Externalization, connections, influence',
    5: 'Universalization - Projection, leadership, teaching',
    6: 'Transition - Objectivity, wisdom, role model'
  };

  return {
    line: lineNumber,
    meaning: lineInterpretations[lineNumber] || 'Unknown',
    hexagram: HEXAGRAMS[hexagramNumber]
  };
}

/**
 * Calculate compatibility using astrological data
 * More accurate than simple hexagram comparison
 *
 * NOTE: Currently unused - reserved for future relationship insights feature
 *
 * @param {Object} profile1 - First person's astrological profile
 * @param {Object} profile2 - Second person's astrological profile
 * @returns {Object} Compatibility insights
 */
export function calculateAstrologicalCompatibility(profile1, profile2) {
  if (!profile1?.astrologicalData || !profile2?.astrologicalData) {
    return null;
  }

  const gates1 = profile1.astrologicalData.humanDesignGates;
  const gates2 = profile2.astrologicalData.humanDesignGates;

  // Find electromagnetic connections (gates that "complete" each other)
  const connections = findGateConnections(gates1, gates2);

  // Analyze elemental harmony from Four Pillars
  const elementalHarmony = analyzeElementalHarmony(
    profile1.astrologicalData.fourPillars,
    profile2.astrologicalData.fourPillars
  );

  return {
    gateConnections: connections,
    elementalHarmony,
    overallCompatibility: calculateOverallScore(connections, elementalHarmony),
    insights: generateCompatibilityInsights(connections, elementalHarmony)
  };
}

/**
 * Find gate connections between two charts
 */
function findGateConnections(gates1, gates2) {
  const connections = [];

  for (const [planet1, data1] of Object.entries(gates1)) {
    for (const [planet2, data2] of Object.entries(gates2)) {
      if (data1.gate && data2.gate) {
        // Check if gates are complementary (this is simplified)
        if (Math.abs(data1.gate - data2.gate) === 32) {
          connections.push({
            type: 'electromagnetic',
            planet1,
            planet2,
            gate1: data1.gate,
            gate2: data2.gate
          });
        }
      }
    }
  }

  return connections;
}

/**
 * Analyze elemental harmony between two Four Pillars charts
 */
function analyzeElementalHarmony(pillars1, pillars2) {
  const elem1 = pillars1.elements.dominant;
  const elem2 = pillars2.elements.dominant;

  // Five Elements cycle
  const productive = {
    'Wood': 'Fire',
    'Fire': 'Earth',
    'Earth': 'Metal',
    'Metal': 'Water',
    'Water': 'Wood'
  };

  const destructive = {
    'Wood': 'Earth',
    'Earth': 'Water',
    'Water': 'Fire',
    'Fire': 'Metal',
    'Metal': 'Wood'
  };

  let relationship = 'neutral';
  if (productive[elem1] === elem2) relationship = 'productive';
  if (destructive[elem1] === elem2) relationship = 'challenging';
  if (elem1 === elem2) relationship = 'similar';

  return {
    person1Element: elem1,
    person2Element: elem2,
    relationship,
    harmony: relationship === 'productive' ? 80 : relationship === 'similar' ? 70 : relationship === 'neutral' ? 50 : 30
  };
}

/**
 * Calculate overall compatibility score
 */
function calculateOverallScore(connections, elementalHarmony) {
  const connectionScore = connections.length * 15;
  const elementScore = elementalHarmony.harmony;

  return Math.min(Math.round((connectionScore + elementScore) / 2), 100);
}

/**
 * Generate human-readable compatibility insights
 */
function generateCompatibilityInsights(connections, elementalHarmony) {
  const insights = [];

  if (connections.length > 0) {
    insights.push(`Strong electromagnetic connections through ${connections.length} gate pair(s)`);
  }

  if (elementalHarmony.relationship === 'productive') {
    insights.push(`Harmonious elemental flow: ${elementalHarmony.person1Element} nourishes ${elementalHarmony.person2Element}`);
  } else if (elementalHarmony.relationship === 'challenging') {
    insights.push(`Growth through differences: ${elementalHarmony.person1Element} and ${elementalHarmony.person2Element} create dynamic tension`);
  }

  return insights;
}

export default {
  calculateAstrologicalProfile,
  calculatePlanetaryPositions,
  calculateHumanDesignGates,
  calculateFourPillars,
  calculateWesternAstrology,
  getCurrentAstrologicalGates,
  interpretLine,
  calculateAstrologicalCompatibility
};
